/**
 * ⭐⭐ WHAT A HOLD YEAR EARNS WHILE THE BUILDING IS STILL EMPTY.
 *
 * 🚨 THE DEFECT THIS EXISTS FOR. `va_vacancy_leaseup` was a BOOLEAN CHECKBOX with a free-text
 * note. It is in the type, validated in the backend, rendered on the Value-Add tab, printed in the
 * PDF as an attributed driver, and set to `true` automatically by the archetype literally named
 * "Value-add / lease-up" - and it was read by NO CALCULATION ANYWHERE. `generateProjections` had
 * no lease-up, downtime or vacancy-ramp concept at all.
 *
 * So the one archetype built for vacant buildings ticked a box saying the deal has a lease-up and
 * then priced it as though it stabilised on the day of close. Measured on Jackson Mobility -
 * 23735 Jackson Ave, a vacant owner-user box run through the front door 2026-08-30:
 *
 *     his delivered analysis   IRR -12.9%   EM 0.49x   (year 1 = $0, full debt-service carry)
 *     the app                  IRR  -9.51%  EM 0.62x   (year 1 = $217,414, fully stabilised)
 *
 * **3.4 points of IRR in the flattering direction, on the one deal whose entire risk is the
 * lease-up.** Ninth occurrence of this build's defect class and the purest of them: a field that
 * describes a risk and prices none of it.
 *
 * ⭐⭐ THE GROWTH CLOCK STARTS AT STABILISATION, NOT AT THE CLOSE. His year 2 is the stabilised
 * NOI *unescalated* - $217,414 - and year 3 is that times 1.03. The rent you sign at lease-up is
 * today's market rent; it escalates from the lease you just signed, not from a close where the
 * building was empty. Simply prorating a growth series that started at close would put year 2 at
 * $223,936 and be wrong by a full year of escalation for the rest of the hold.
 *
 * ⛔ ZERO MONTHS IS EXACTLY TODAY'S BEHAVIOUR - `occupiedShare` 1 and `growthPeriods` year-1 - so
 * every deal that has no lease-up is untouched. That is pinned as a test, not assumed.
 */

export interface LeaseUpYearShare {
  /** Fraction of this hold year the building is stabilised and producing. 0 to 1. */
  occupiedShare: number;
  /**
   * Whole years of growth to compound, counted FROM STABILISATION. ⛔ Not `year - 1`: that is the
   * close, and on a lease-up the close is not when the rent starts escalating.
   */
  growthPeriods: number;
}

/**
 * @param year 1-indexed hold year.
 * @param leaseUpMonths Months from the close during which the building produces no income.
 */
export const leaseUpYearShare = (year: number, leaseUpMonths: number): LeaseUpYearShare => {
  const months = Number.isFinite(leaseUpMonths) && leaseUpMonths > 0 ? leaseUpMonths : 0;
  const occupiedMonths = Math.min(12, Math.max(0, year * 12 - months));
  return {
    occupiedShare: occupiedMonths / 12,
    // ⭐ Whole lease-up years shift the clock; a stub (9 months) does not delay escalation a
    // second time - the tenant is in place and paying inside that first year.
    growthPeriods: Math.max(0, year - 1 - Math.floor(months / 12)),
  };
};
